/**
 * DevLogin - Quick login for devs using pre-seeded test accounts
 */
import { authLogger } from '@annote/core/utils/debug';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useDataContext } from 'src/context/DataContext';
import { isSupabaseDatabase } from '@annote/core/dbs/supabaseDb';

// Treat Vite dev server as dev, regardless of custom mode (e.g., 'devcloud')
const DEV_MODE: boolean = Boolean(
  import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'devcloud',
);

// Pre-seeded test accounts (must match seed_dev_users.sql)
const DEV_ACCOUNTS = [
  { email: 'davefowler@gmail.com', label: 'Dave Fowler' },
  { email: 'test-dev@annote.com', label: 'Dev Account' },
  { email: 'test1@annote.com', label: 'Test Account 1' },
  { email: 'test2@annote.com', label: 'Test Account 2' },
  { email: 'test-designer@annote.com', label: 'Designer Account' },
  { email: 'test-qa@annote.com', label: 'QA Account' },
] as const;

const DEV_PASSWORD = 'devpassword123';

const DevLoginWrapper = styled.div`
  padding-top: 10rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 2rem;
`;

const DevLoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  max-width: 400px;
  width: 100%;
`;

const AccountButton = styled.button<{ $selected: boolean }>`
  padding: 1rem 1.5rem;
  font-size: 1rem;
  background-color: ${(props) => (props.$selected ? '#4285f4' : 'white')};
  color: ${(props) => (props.$selected ? 'white' : '#333')};
  border: 2px solid ${(props) => (props.$selected ? '#4285f4' : '#e0e0e0')};
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  max-width: 400px;
  text-align: left;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &:hover {
    border-color: #4285f4;
    background-color: ${(props) => (props.$selected ? '#357ae8' : '#f5f5f5')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AccountLabel = styled.div`
  font-weight: 600;
`;

const AccountEmail = styled.div`
  font-size: 0.875rem;
  opacity: 0.8;
`;

const DevMessage = styled.div`
  font-size: 1rem;
  color: #666;
  text-align: center;
  max-width: 400px;
  line-height: 1.4;
`;

const ErrorMessage = styled.div`
  font-size: 1rem;
  color: #d32f2f;
  text-align: center;
  max-width: 400px;
  line-height: 1.4;
  background-color: #ffebee;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #ffcdd2;
`;

const SuccessMessage = styled.div`
  font-size: 1rem;
  color: #2e7d32;
  text-align: center;
  max-width: 400px;
  line-height: 1.4;
  background-color: #e8f5e8;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #c8e6c9;
`;

const DevLogin: React.FC = () => {
  const navigate = useNavigate();
  const { annoteDB } = useDataContext();
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isWaitingForDB, setIsWaitingForDB] = useState(true);

  // Wait for database to be ready
  useEffect(() => {
    if (annoteDB) {
      setIsWaitingForDB(false);
      return undefined;
    }

    // Keep waiting
    const timer = setTimeout(() => {
      if (!annoteDB) {
        setError('Database is taking longer than expected to initialize. Please check if the backend is running.');
        setIsWaitingForDB(false);
      }
    }, 30000); // 30 second timeout
    return (): void => clearTimeout(timer);
  }, [annoteDB]);

  // Redirect if not in development mode
  useEffect(() => {
    if (!DEV_MODE) {
      navigate('/login');
    }
  }, [navigate]);

  const handleDevLogin = async (email: string): Promise<void> => {
    if (!DEV_MODE) {
      setError('Development login is only available in development mode');
      return;
    }

    if (!annoteDB) {
      setError('Database not ready yet. Please wait a moment and try again.');
      return;
    }

    const { serverDB } = annoteDB;
    if (!serverDB || !isSupabaseDatabase(serverDB)) {
      setError('Supabase database not available');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setSelectedEmail(email);

    try {
      // Use regular Supabase password authentication
      const supabaseClient = serverDB.db;

      const { data: authData, error: authError } = await supabaseClient.auth.signInWithPassword({
        email,
        password: DEV_PASSWORD,
      });

      if (authError) {
        throw new Error(`Login failed: ${authError.message}`);
      }

      if (!authData.user) {
        throw new Error('No user returned from authentication');
      }

      // Remember the email for next time
      localStorage.setItem('devLoginEmail', email);

      // Initialize the database and fetch profile
      await serverDB.init();

      // Profile is now available in annoteDB.profile after init
      if (!annoteDB.profile) {
        throw new Error('Failed to fetch profile after login');
      }

      setSuccess('Login successful! Redirecting...');

      // Redirect after a short delay to show success message
      // Use a default authenticated route instead of '/' which shows ComingSoon
      setTimeout(() => {
        const redirectTo = localStorage.getItem('redirectTo');
        localStorage.removeItem('redirectTo');
        // Default to /focus (an authenticated route) instead of / (ComingSoon)
        navigate(redirectTo || '/focus');
      }, 500);
    } catch (err) {
      authLogger.error('Dev login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';

      // Provide helpful error message if account doesn't exist
      if (errorMessage.includes('Invalid login credentials')) {
        setError(
          `Test account not found. Make sure to run the seed_dev_users.sql script on your dev database. ` +
            `Error: ${errorMessage}`,
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!DEV_MODE) {
    return null;
  }

  return (
    <DevLoginWrapper>
      <DevMessage>
        <strong>Development Login</strong>
        <br />
        <small>Select a test account to log in</small>
        <br />
        <small style={{ fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
          Password: <code>{DEV_PASSWORD}</code>
        </small>
      </DevMessage>

      {isWaitingForDB && !error && (
        <DevMessage>
          <small>⏳ Waiting for backend to be ready...</small>
        </DevMessage>
      )}

      <DevLoginForm>
        {DEV_ACCOUNTS.map((account) => (
          <AccountButton
            key={account.email}
            type="button"
            $selected={selectedEmail === account.email && isLoading}
            onClick={() => handleDevLogin(account.email)}
            disabled={isLoading || isWaitingForDB}
          >
            <AccountLabel>{account.label}</AccountLabel>
            <AccountEmail>{account.email}</AccountEmail>
          </AccountButton>
        ))}
      </DevLoginForm>

      {error && <ErrorMessage>Error: {error}</ErrorMessage>}

      {success && <SuccessMessage>{success}</SuccessMessage>}
    </DevLoginWrapper>
  );
};

export default DevLogin;
